import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import {
  ShieldCheck,
  ShieldOff,
  Loader2,
  KeyRound,
  Copy,
  Download,
  AlertCircle,
  CheckCircle2,
  Smartphone
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSettings = () => {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  // mode: 'idle' | 'setup' | 'backup' (showing codes once) | 'disable'
  const [mode, setMode] = useState('idle');
  const [setupData, setSetupData] = useState(null);
  const [enableCode, setEnableCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/auth/2fa/status`);
      setStatus(res.data);
    } catch (e) {
      console.error('Error fetching 2FA status:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const resetMessages = () => { setError(''); setSuccess(''); };

  const startSetup = async () => {
    resetMessages();
    setBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/2fa/setup`);
      setSetupData(res.data);
      setMode('setup');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to start 2FA setup');
    } finally {
      setBusy(false);
    }
  };

  const confirmEnable = async (e) => {
    e.preventDefault();
    resetMessages();
    setBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/2fa/enable`, { code: enableCode });
      setBackupCodes(res.data.backup_codes || []);
      setMode('backup');
      setEnableCode('');
      setSetupData(null);
      await fetchStatus();
    } catch (e) {
      setError(e.response?.data?.detail || 'Invalid verification code');
    } finally {
      setBusy(false);
    }
  };

  const confirmDisable = async (e) => {
    e.preventDefault();
    resetMessages();
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/auth/2fa/disable`, { code: disableCode });
      setDisableCode('');
      setMode('idle');
      setSuccess('Two-factor authentication has been disabled.');
      await fetchStatus();
    } catch (e) {
      setError(e.response?.data?.detail || 'Invalid verification code');
    } finally {
      setBusy(false);
    }
  };

  const cancelFlow = () => {
    setMode('idle');
    setSetupData(null);
    setEnableCode('');
    setDisableCode('');
    resetMessages();
  };

  const copyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    setSuccess('Backup codes copied to clipboard.');
  };

  const downloadBackupCodes = () => {
    const blob = new Blob(
      [`Coinexus Admin - 2FA Backup Codes\nAccount: ${user?.email}\n\n${backupCodes.join('\n')}\n\nEach code can be used once.`],
      { type: 'text/plain' }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'coinexus-2fa-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AdminLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white mb-2 flex items-center gap-2" data-testid="settings-title">
          <KeyRound className="w-6 h-6 text-amber-400" />
          Security Settings
        </h1>
        <p className="text-slate-400">Manage two-factor authentication for your account.</p>
      </div>

      <div className="max-w-2xl">
        {/* Alerts */}
        {error && (
          <div data-testid="settings-error" className="flex items-center gap-3 p-4 mb-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm">{error}</span>
          </div>
        )}
        {success && (
          <div data-testid="settings-success" className="flex items-center gap-3 p-4 mb-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-emerald-400">
            <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm">{success}</span>
          </div>
        )}

        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
            </div>
          ) : (
            <>
              {/* Status header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  {status?.enabled ? (
                    <div className="p-2 rounded-lg bg-emerald-500/20 text-emerald-400"><ShieldCheck className="w-6 h-6" /></div>
                  ) : (
                    <div className="p-2 rounded-lg bg-slate-500/20 text-slate-400"><ShieldOff className="w-6 h-6" /></div>
                  )}
                  <div>
                    <h2 className="text-lg font-semibold text-white">Two-Factor Authentication</h2>
                    <p className="text-sm text-slate-400" data-testid="twofa-status-text">
                      {status?.enabled
                        ? `Enabled · ${status.backup_codes_remaining} backup codes remaining`
                        : 'Not enabled — add an extra layer of security to your account.'}
                    </p>
                  </div>
                </div>
                <span
                  data-testid="twofa-status-badge"
                  className={`px-3 py-1 text-xs font-medium rounded border ${
                    status?.enabled
                      ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
                      : 'bg-slate-500/20 text-slate-400 border-slate-500/30'
                  }`}
                >
                  {status?.enabled ? 'ON' : 'OFF'}
                </span>
              </div>

              {/* IDLE: enable or disable buttons */}
              {mode === 'idle' && (
                <div>
                  {status?.enabled ? (
                    <button
                      onClick={() => { resetMessages(); setMode('disable'); }}
                      data-testid="disable-2fa-btn"
                      className="inline-flex items-center gap-2 px-4 py-2.5 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                    >
                      <ShieldOff className="w-4 h-4" /> Disable 2FA
                    </button>
                  ) : (
                    <button
                      onClick={startSetup}
                      disabled={busy}
                      data-testid="enable-2fa-btn"
                      className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 font-semibold rounded-lg hover:from-amber-400 hover:to-amber-500 transition-all disabled:opacity-50"
                    >
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                      Enable 2FA
                    </button>
                  )}
                </div>
              )}

              {/* SETUP: show QR + secret, confirm with code */}
              {mode === 'setup' && setupData && (
                <form onSubmit={confirmEnable} className="space-y-5">
                  <div className="flex items-start gap-2 text-slate-300 text-sm">
                    <Smartphone className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <span>Scan this QR code with Google Authenticator, Microsoft Authenticator, or Authy.</span>
                  </div>
                  <div className="flex flex-col items-center gap-3 p-4 bg-white rounded-lg w-fit">
                    <img src={setupData.qr_code} alt="2FA QR code" width={180} height={180} data-testid="twofa-qr" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase mb-1">Or enter this key manually</p>
                    <code data-testid="twofa-secret" className="block px-3 py-2 bg-slate-900/70 border border-slate-700 rounded text-amber-300 text-sm break-all font-mono">
                      {setupData.secret}
                    </code>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Enter the 6-digit code to confirm</label>
                    <input
                      type="text"
                      value={enableCode}
                      onChange={(e) => setEnableCode(e.target.value)}
                      placeholder="123456"
                      required
                      data-testid="twofa-setup-code-input"
                      className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 tracking-widest focus:outline-none focus:border-amber-500"
                    />
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="submit"
                      disabled={busy}
                      data-testid="twofa-enable-confirm"
                      className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 font-semibold rounded-lg hover:from-amber-400 hover:to-amber-500 transition-all disabled:opacity-50"
                    >
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                      Verify & Enable
                    </button>
                    <button type="button" onClick={cancelFlow} className="px-4 py-2.5 text-slate-400 hover:text-white bg-slate-700/50 hover:bg-slate-700 rounded-lg transition-colors">
                      Cancel
                    </button>
                  </div>
                </form>
              )}

              {/* BACKUP: show backup codes once */}
              {mode === 'backup' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-emerald-400">
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
                    <span className="text-sm">2FA enabled! Save these backup codes now — they will not be shown again.</span>
                  </div>
                  <div data-testid="backup-codes-list" className="grid grid-cols-2 gap-2 p-4 bg-slate-900/70 border border-slate-700 rounded-lg">
                    {backupCodes.map((c) => (
                      <code key={c} className="text-amber-300 font-mono text-sm text-center py-1">{c}</code>
                    ))}
                  </div>
                  <div className="flex gap-3">
                    <button onClick={copyBackupCodes} data-testid="copy-backup-codes" className="inline-flex items-center gap-2 px-4 py-2 bg-slate-700/50 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors text-sm">
                      <Copy className="w-4 h-4" /> Copy
                    </button>
                    <button onClick={downloadBackupCodes} data-testid="download-backup-codes" className="inline-flex items-center gap-2 px-4 py-2 bg-slate-700/50 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors text-sm">
                      <Download className="w-4 h-4" /> Download
                    </button>
                    <button onClick={() => { setMode('idle'); setBackupCodes([]); }} data-testid="twofa-done-btn" className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 text-slate-900 font-semibold rounded-lg hover:bg-amber-400 transition-colors text-sm ml-auto">
                      Done
                    </button>
                  </div>
                </div>
              )}

              {/* DISABLE: confirm with code */}
              {mode === 'disable' && (
                <form onSubmit={confirmDisable} className="space-y-4">
                  <p className="text-sm text-slate-300">Enter a current authenticator code or a backup code to disable 2FA.</p>
                  <input
                    type="text"
                    value={disableCode}
                    onChange={(e) => setDisableCode(e.target.value)}
                    placeholder="123456 or backup code"
                    required
                    data-testid="twofa-disable-code-input"
                    className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 tracking-widest focus:outline-none focus:border-amber-500"
                  />
                  <div className="flex gap-3">
                    <button
                      type="submit"
                      disabled={busy}
                      data-testid="twofa-disable-confirm"
                      className="inline-flex items-center gap-2 px-4 py-2.5 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors disabled:opacity-50"
                    >
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldOff className="w-4 h-4" />}
                      Confirm Disable
                    </button>
                    <button type="button" onClick={cancelFlow} className="px-4 py-2.5 text-slate-400 hover:text-white bg-slate-700/50 hover:bg-slate-700 rounded-lg transition-colors">
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
